import { Button, FileInput, Label, Spinner, TextInput, Textarea } from 'flowbite-react';
import React, { FormEvent, useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { sendOfferLetter } from 'src/apis/application';
import { getPolicyForS3NonVideo } from 'src/apis/user';
import { useToast } from 'src/context/ToastProvider';
import { handleS3Upload } from 'src/utils/imageUpload';
import { OfferedApplicationParameter, SendOfferLetterInput, UserISAStatus, WorkingMode } from 'types';
import { MobileDatePicker } from '@mui/x-date-pickers';
import { Dropdown, camelCaseToSpace, isUserRecruiter, mapPositionTitlesToDropdownOptions } from 'shared-utils';
import Select, { SingleValue } from 'react-select';
import dayjs from 'dayjs';
import { useGetPositionByIdQuery } from 'src/hooks/position.hooks';
import { useGetPositionTitlesQuery } from '../../hooks/positionConfiguration.hooks';
import { IoMdAdd, IoMdRemove } from 'react-icons/io';
import { useUserContext } from 'src/context/UserProvider';

interface Props {
	applicationId: string;
	userName: string;
	positionId: string;
	adminRejectionRemarks?: string;
	adminRejectedOfferLetter?: boolean;
	onSubmit?: (data: SendOfferLetterInput) => void;
	onFinish?: () => void;
	offeredPositionParameters?: OfferedApplicationParameter;
	offerLetterLink?: string;
}

const RecruiterCandidateSendOfferLetter = ({
	applicationId,
	userName,
	onSubmit,
	onFinish,
	positionId,
	adminRejectionRemarks,
	adminRejectedOfferLetter,
	offeredPositionParameters,
	offerLetterLink,
}: Props) => {
	const toast = useToast();
	const { isRecruiterLoggedIn, isDriveAdminLoggedIn } = useUserContext();
	const queryClient = useQueryClient();
	const [file, setFile] = React.useState<File | null>();
	const [isLoading, setIsLoading] = React.useState(false);
	const [offerLetterInput, setOfferLetterInput] = React.useState<SendOfferLetterInput>({
		positionId,
		applicationId,
		offerLetterLink: '',
		remarks: '',
		offeredPositionParameters,
	});
	const { isLoading: isPositionLoading, data: positionData } = useGetPositionByIdQuery(positionId, data => {
		if (!offeredPositionParameters?.offerType)
			setOfferLetterInput({
				...offerLetterInput,
				offeredPositionParameters: {
					...offerLetterInput?.offeredPositionParameters,
					offerType: data?.position?.workingMode,
				} as OfferedApplicationParameter,
			});
	});
	const { data: positionTitlesData, isLoading: isLoadingPositionTitles } = useGetPositionTitlesQuery();

	useEffect(() => {
		setOfferLetterInput(prev => {
			return {
				...prev,
				offeredPositionParameters: offeredPositionParameters,
			};
		});
	}, [offeredPositionParameters]);

	const sendOfferLetterFn = useMutation(['sendOfferLetter'], sendOfferLetter, {
		onSuccess() {
			toast({
				title: userName ? `Offer sent to ${userName}` : 'Offer sent',
				type: 'success',
			});
			setOfferLetterInput({
				positionId,
				applicationId,
				remarks: '',
				offerLetterLink: '',
				offeredPositionParameters,
			});
			queryClient.refetchQueries({ queryKey: ['getRecommendationsForPosition'], exact: false });
			setFile(null);
			onFinish && onFinish();
		},
		onError(error: Error) {
			toast({
				title: `Error occured ${error.message}`,
				type: 'error',
			});
		},
	});

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		let imageUrl = '';
		if (file) {
			const fileSizeMB = file.size / (1024 * 1024);
			if (fileSizeMB > 5) {
				toast({
					type: 'error',
					title: 'File size exceeds the maximum limit of 5 MB',
					duration: 10000,
				});
				return;
			}

			setIsLoading(true);

			const policy = await getPolicyForS3NonVideo(
				file.name.trim().split(' ').join('_'),
				'offerLetters',
				file.type,
				true,
			);
			const uploadedUrl = await handleS3Upload(policy, file);
			setIsLoading(false);
			if (uploadedUrl) imageUrl = uploadedUrl;
		}
		const updatedOfferLetterLink = imageUrl || offerLetterLink || '';
		console.log('offerLetterInput:', offerLetterInput);
		if (onSubmit)
			onSubmit({
				...offerLetterInput,
				offerLetterLink: updatedOfferLetterLink,
			});
		else {
			sendOfferLetterFn.mutate({
				...offerLetterInput,
				offerLetterLink: updatedOfferLetterLink,
			});
		}
	};

	const isInterShipPosition =
		offerLetterInput?.offeredPositionParameters?.offerType &&
		(offerLetterInput?.offeredPositionParameters?.offerType === WorkingMode.INTERNSHIP ||
			offerLetterInput?.offeredPositionParameters?.offerType === WorkingMode.INTERNSHIP_TO_FULL_TIME);

	return (
		<>
			{adminRejectedOfferLetter && adminRejectionRemarks ? (
				<div className="border border-[#C7C9D933] dark:bg-[#18222D] bg-[#E5F0FF] mt-[14px] rounded-[8px] p-[10px]">
					<p className="text-[#555770]">
						<span className="text-yellow-600">Admin Comments: </span>
						{adminRejectionRemarks}
					</p>
				</div>
			) : null}
			<form className="relative" onSubmit={handleSubmit}>
				<div className="relative mt-[16px]  ">
					<div className="mb-[8px]">
						<Label>{'Upload Offer '}</Label>
						{offerLetterLink ? (
							<a href={offerLetterLink} target="_blank" className="text-[#7777E7] underline text-xs ml-1">
								(Preview){' '}
							</a>
						) : (
							<span className="mt-2 text-xs italic text-gray-500 dark:text-gray-400">(Optional)</span>
						)}
					</div>

					<FileInput
						id="hukumu-profile-resume-file-input"
						accept=".pdf,doc,docx"
						disabled={isLoading}
						helperText="Only txt, pdf, doc files allowed."
						className="w-full text-xs font-normal italic min-h-[42px] [&~p]:italic [&_input]:bg-white dark:[&_input]:bg-inherit"
						onChange={event => {
							if (event.target.files) {
								console.log('event.target.files[0]:', event.target.files[0]);
								setFile && setFile(event.target.files[0]);
							}
						}}
					/>
				</div>

				<div className="mt-[16px] whiteBgSelect">
					<div className="mb-[8px]">
						<Label>{'Designation offered:'}</Label>
						<span className="text-[red]">*</span>
					</div>

					<Select
						options={mapPositionTitlesToDropdownOptions(positionTitlesData?.positionTitles)}
						isLoading={isLoadingPositionTitles}
						menuPlacement={'auto'}
						classNamePrefix="my-select-plans"
						className="single-value-select-tag"
						onChange={(val: SingleValue<Dropdown>) => {
							setOfferLetterInput({
								...offerLetterInput,
								offeredPositionParameters: {
									...offerLetterInput?.offeredPositionParameters,
									positionTitle: val?.label ?? '',
								} as OfferedApplicationParameter,
							});
						}}
						value={mapPositionTitlesToDropdownOptions(positionTitlesData?.positionTitles)?.find(item => {
							return item.label === offerLetterInput.offeredPositionParameters?.positionTitle;
						})}
						placeholder="SDE-1"
					/>
				</div>

				<div className="mt-[16px] whiteBgSelect">
					<div className="mb-[8px]">
						<Label>{'Job Type'}</Label>
						<span className="text-[red]">*</span>
					</div>

					<Select
						options={Object.values(WorkingMode).map(mode => ({ label: camelCaseToSpace(mode), value: mode }))}
						menuPlacement={'auto'}
						classNamePrefix="my-select-plans"
						className="single-value-select-tag [&_my-select-plans__control]:!bg-white"
						onChange={(val: SingleValue<Dropdown>) => {
							setOfferLetterInput({
								...offerLetterInput,
								offeredPositionParameters: {
									...offerLetterInput?.offeredPositionParameters,
									offerType: val?.value ?? '',
									ctc: 0,
									internshipCtc: 0,
									internshipDuration: 0,
								} as OfferedApplicationParameter,
							});
						}}
						value={
							offerLetterInput?.offeredPositionParameters?.offerType
								? {
									label: camelCaseToSpace(offerLetterInput?.offeredPositionParameters?.offerType),
									value: offerLetterInput?.offeredPositionParameters?.offerType,
								}
								: undefined
						}
						placeholder="Job type"
					/>
				</div>

				{offerLetterInput.offeredPositionParameters?.offerType !== WorkingMode.INTERNSHIP ? (
					<div className="mt-[16px]">
						<div className="mb-[8px]">
							<Label>{'CTC offered in LPA (in INR)'}</Label>
							<span className="text-[red]">*</span>
						</div>

						<TextInput
							name="ctc"
							min={1}
							max={100}
							step="any"
							type="number"
							value={offerLetterInput.offeredPositionParameters?.ctc}
							onWheel={e => document.activeElement instanceof HTMLElement && document.activeElement.blur()}
							placeholder="Enter CTC (ex: 8, 20)"
							className="[&_input]:bg-white dark:[&_input]:bg-inherit dark:text-white dark:border-[#4B5563] rounded-lg dark:bg-[#374151] focus:border-transparent focus:!ring-[1px] focus:!ring-[#7777E7]"
							onChange={e => {
								setOfferLetterInput({
									...offerLetterInput,
									offeredPositionParameters: {
										...offerLetterInput?.offeredPositionParameters,
										ctc: Number(e.target.value),
									} as OfferedApplicationParameter,
								});
							}}
						></TextInput>
					</div>
				) : null}

				{isInterShipPosition ? (
					<div className="mt-[16px]">
						<div className="mb-[8px]">
							<Label>{'Internship Salary (per month INR)(ex: 5k, 10k)'}</Label>
							<span className="text-red-600">*</span>
						</div>

						<div className="flex items-center gap-2">
							<TextInput
								name="ctc"
								step="any"
								type="number"
								min={0}
								max={100}
								value={offerLetterInput?.offeredPositionParameters?.internshipCtc}
								onWheel={e => document.activeElement instanceof HTMLElement && document.activeElement.blur()}
								placeholder="6"
								className="[&_input]:bg-white dark:[&_input]:bg-inherit [&_input:focus]:ring-transparent [&_input:focus]:border-[#1C64F2] [&_input]:text-center RemoveInputArrow max-w-[50px]"
								onChange={e => {
									if (Number(e.target.value) <= 100000) {
										setOfferLetterInput({
											...offerLetterInput,
											offeredPositionParameters: {
												...offerLetterInput?.offeredPositionParameters,
												internshipCtc: Number(e.target.value),
												ctc:
													offerLetterInput.offeredPositionParameters?.offerType !== WorkingMode.INTERNSHIP
														? offerLetterInput?.offeredPositionParameters?.ctc
														: 0,
											} as OfferedApplicationParameter,
										});
									}
								}}
							></TextInput>
							K
						</div>
					</div>
				) : null}

				{isInterShipPosition ? (
					<div className="mt-[16px]">
						<div className="mb-[8px]">
							<Label>{'Internship Duration (in months)'}</Label>
							<span className="text-red-600">*</span>
						</div>
						<div className="flex items-center gap-[8px]">
							<Button
								className="text-[#1C1C28] rounded-[8px] p-[8px] [&>span]:!p-0"
								color="light"
								disabled={
									(offerLetterInput?.offeredPositionParameters?.internshipDuration !== undefined &&
										offerLetterInput?.offeredPositionParameters?.internshipDuration <= 1) ||
									offerLetterInput?.offeredPositionParameters?.internshipDuration === undefined
								}
								onClick={() => {
									setOfferLetterInput({
										...offerLetterInput,
										offeredPositionParameters: {
											...offerLetterInput?.offeredPositionParameters,
											internshipDuration:
												offerLetterInput?.offeredPositionParameters?.internshipDuration !== undefined
													? offerLetterInput?.offeredPositionParameters?.internshipDuration - 1
													: 1,
										} as OfferedApplicationParameter,
									});
								}}
							>
								<IoMdRemove className="w-[24px] h-[24px]" />
							</Button>

							<TextInput
								min={1}
								max={12}
								onWheel={e => document.activeElement instanceof HTMLElement && document.activeElement.blur()}
								type="number"
								placeholder="2"
								name="internshipDuration"
								value={offerLetterInput?.offeredPositionParameters?.internshipDuration || 0}
								onChange={event => {
									if (+event?.target?.value <= 12) {
										setOfferLetterInput({
											...offerLetterInput,
											offeredPositionParameters: {
												...offerLetterInput?.offeredPositionParameters,
												internshipDuration: +(event?.target?.value || 0),
											} as OfferedApplicationParameter,
										});
									}
								}}
								className="[&_input:focus]:ring-transparent [&_input:focus]:border-[#1C64F2] [&_input]:text-center RemoveInputArrow"
							/>

							<Button
								className="text-[#1C1C28] rounded-[8px] p-[8px] [&>span]:!p-0"
								color="light"
								disabled={
									offerLetterInput?.offeredPositionParameters?.internshipDuration !== undefined &&
									offerLetterInput?.offeredPositionParameters?.internshipDuration >= 12
								}
								onClick={() => {
									setOfferLetterInput({
										...offerLetterInput,
										offeredPositionParameters: {
											...offerLetterInput?.offeredPositionParameters,
											internshipDuration:
												offerLetterInput?.offeredPositionParameters?.internshipDuration !== undefined
													? offerLetterInput?.offeredPositionParameters?.internshipDuration + 1
													: 1,
										} as OfferedApplicationParameter,
									});
								}}
							>
								<IoMdAdd className="w-[24px] h-[24px]" />
							</Button>
						</div>
					</div>
				) : null}

				<div className="mt-[16px]">
					<div className="mb-[8px]">
						<Label>{'Date of Joining'}</Label>
						<span className="text-[red]">*</span>
					</div>

					<MobileDatePicker
						defaultValue={undefined}
						value={
							offerLetterInput?.offeredPositionParameters?.dateOfJoining
								? dayjs(offerLetterInput?.offeredPositionParameters?.dateOfJoining)
								: undefined
						}
						// minDate={dayjs()}
						format="DD MMM, YYYY"
						onChange={date => {
							const dateValue = date as { $d?: Date };
							setOfferLetterInput({
								...offerLetterInput,
								offeredPositionParameters: {
									...offerLetterInput?.offeredPositionParameters,
									dateOfJoining: dayjs(dateValue?.$d).toDate(),
								} as OfferedApplicationParameter,
							});
						}}
						sx={{
							'&': {
								width: '100%',
							},
							'& .MuiInputBase-input': {
								padding: '10px',
								borderRadius: '8px',
								backgroundColor: 'white',
								border: '1px solid rgb(209 213 219 / 1)',
								fontSize: '14px',
							},
							'& .MuiInputBase-root': {
								outline: 'none',
								padding: '0px',
								'&:hover': {
									outline: 'none',
								},
							},
							'& fieldset': {
								display: 'none',
							},
						}}
					/>
				</div>

				{!isRecruiterLoggedIn || isDriveAdminLoggedIn ? (
					<div className="mt-[16px] whiteBgSelect">
						<div className="mb-[8px]">
							<Label>{'ISA Status'}</Label>
							<span className="text-[red]">*</span>
						</div>

						<Select
							options={Object.values(UserISAStatus).map(status => ({
								label: status,
								value: status,
							}))}
							menuPlacement={'auto'}
							classNamePrefix="my-select-plans"
							className="single-value-select-tag [&_my-select-plans__control]:!bg-white"
							onChange={(val: SingleValue<Dropdown>) => {
								setOfferLetterInput({
									...offerLetterInput,
									offeredPositionParameters: {
										...offerLetterInput?.offeredPositionParameters,
										isaStatus: val?.value,
									} as OfferedApplicationParameter,
								});
							}}
							value={
								offerLetterInput?.offeredPositionParameters?.isaStatus
									? {
										label: offerLetterInput?.offeredPositionParameters?.isaStatus,
										value: offerLetterInput?.offeredPositionParameters?.isaStatus,
									}
									: null
							}
							placeholder="ISA Status"
						/>
					</div>
				) : null}

				<div className="mt-[16px]">
					<div className="mb-[8px] flex justify-between items-end">
						<div>
							<Label>{'Your message '}</Label>
						</div>

						<span className="italic text-xs text-[#6B7280] ">
							{(offerLetterInput.offeredPositionParameters?.remarks?.length || 0) + ' / ' + 640}
						</span>
					</div>

					<Textarea
						name="message"
						maxLength={640}
						value={offerLetterInput.offeredPositionParameters?.remarks}
						placeholder="Write your message here ..."
						className="border bg-white dark:text-white dark:border-[#4B5563] min-h-[185px] dark:bg-[#374151] focus:border-transparent focus:!ring-[1px] focus:!ring-[#7777E7]"
						onChange={e => {
							setOfferLetterInput(prev => {
								return {
									...prev,
									offeredPositionParameters: {
										...offerLetterInput?.offeredPositionParameters,
										remarks: e.target.value,
									} as OfferedApplicationParameter,
								};
							});
						}}
					></Textarea>
				</div>

				<div className="sticky bottom-[16px] inset-x-0 w-full transition-all md:max-w-[580px] mx-auto">
					<Button
						type="submit"
						className="w-full mt-[20px] bg-[#7777E7] disabled:opacity-100 disabled:bg-gray-300"
						color="purplePlaced"
						disabled={
							isLoading ||
							sendOfferLetterFn.isLoading ||
							(!isRecruiterLoggedIn && !offerLetterInput?.offeredPositionParameters?.isaStatus) ||
							(offerLetterInput?.offeredPositionParameters?.offerType !== WorkingMode.INTERNSHIP &&
								!offerLetterInput.offeredPositionParameters?.ctc) ||
							!offerLetterInput?.offeredPositionParameters?.offerType ||
							!offerLetterInput.offeredPositionParameters?.positionTitle ||
							!offerLetterInput.offeredPositionParameters?.dateOfJoining ||
							(isInterShipPosition && !offerLetterInput.offeredPositionParameters?.internshipDuration) ||
							(offerLetterInput?.offeredPositionParameters?.offerType === WorkingMode.INTERNSHIP_TO_FULL_TIME &&
								!offerLetterInput.offeredPositionParameters?.internshipCtc)
						}
					>
						{sendOfferLetterFn.isLoading || isLoading ? <Spinner /> : 'Send Offer'}
					</Button>
				</div>
			</form>
		</>
	);
};

export default RecruiterCandidateSendOfferLetter;
